// import cookies from 'js-cookie';
// import config from '@/config';
import { axios } from '@/axios.config';
import { v4 as uuidv4 } from 'uuid';
import display from '@/prototypes/display';

const settings = {
  chart: {
    conditions: {
      owner: 'company',
      ownerId: '',
      start: null,
      end: null,
      category: '',
      granularity: '',
      resource: '',
      vendor: '',
    },
    preferences: {
      currency: 'usd',
      granularity: 'daily',
      category: 'service',

      // Chart type
      defaultChartType: 'stacked',

      // Range
      defaultRange: 'lastMonth',

      // Series
      defaultSeries: { service: [], account: [] },
      defaultSeriesCount: 5,
      nonGroupingSeriesCount: 5,

      // Total
      showTotal: true,
      totalColor: '#ff9800',
      totalStrokeType: 'solid-bullet',
      totalLineWidth: 2,

      // Others
      othersColor: '#009688',

      // Style
      scrollbar: true,
      animated: true,
      grouping: true,

      // Export
      exportDataType: 'csv',
      exportImageType: 'svg',
      exportPrefix: 'chart',

      // Datatable
      defaultTableRowsPerPage: 10,
    },
  },
};

const dimensions = {
  chart: {
    w: 9,
    h: 40,
  },
};

export const state = {
  colNum: 9,
  layout: [],
  dashboards: null,
  currentDashboardId: null,
  errorMessage: '',
};

export const actions = {
  fetchDashboards: async ({ commit, dispatch }) => {
    try {
      const res = await axios.get(`/dashboard/`);
      const dashboards = res.data.data;

      if (dashboards.length) {
        commit(
          'updateDashboards',
          dashboards.map(item => ({
            id: item.id,
            name: item.name,
            isPublic: item.is_public,
            layout: item.widgets,
          }))
        );
        commit('updateCurrentDashboardId', dashboards[0].id);
      } else {
        dispatch('setDefaultDashboard');
      }
    } catch (e) {
      if (e.response.status === 404 && e.response.data.message === 'Not found') {
        dispatch('setDefaultDashboard');
      }
    }
  },

  setDefaultDashboard: async ({ commit, dispatch, rootGetters }) => {
    const resourceTypes = rootGetters['user/getResourceTypes'];

    if (resourceTypes.includes('account') && display('usage_account')) {
      // Resource types has 'account'.
      commit('usageReports/updateGroupBy', 'account', { root: true });
    } else if (resourceTypes.includes('tag') && display('usage_crosstag')) {
      // Resource types has 'tag'.
      commit('usageReports/updateGroupBy', 'cross_tag', { root: true });
    } else if (display('usage_group') && !resourceTypes.includes('tag')) {
      // Resource types has 'group'.
      commit('usageReports/updateGroupBy', 'acc_group', { root: true });
    }

    await dispatch('usageReports/fetchList', { force: true }, { root: true });

    dispatch('saveDashboard', {
      name: 'Dashboard',
      isPublic: true,
      widgets: [
        {
          x: 0,
          y: 0,
          w: dimensions.chart.w,
          h: dimensions.chart.h,
          i: uuidv4(),
          static: true,
          widget: 'chart',
          settings: { ...settings.chart },
        },
      ],
    });
  },

  saveDashboard: async ({ commit, dispatch }, { id, ...args }) => {
    try {
      const params = {
        name: args.name,
        is_public: args.isPublic,
        widgets: args.widgets,
      };

      var res = {};

      if (id) {
        res = await axios.put(`aqua/dashboard/${id}`, params);
      } else {
        res = await axios.post(`aqua/dashboard/`, params);
        if (res.data.success) {
          id = res.data.id;
        }
      }
    } catch (e) {
      // console.error(e); // eslint-disable-line no-console
    }

    await dispatch('fetchDashboards');
    commit('updateCurrentDashboardId', id);
  },

  deleteDashboard: async ({ getters, dispatch }) => {
    try {
      await axios.delete(`/aqua/dashboard/${getters.currentDashboardId}`);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }

    await dispatch('fetchDashboards');
  },

  addWidget({ getters }, widget) {
    const item = {
      x: (getters.layout.length * 2) % (getters.colNum || 9),
      y: getters.layout.length + (getters.colNum || 9), // puts it at the bottom
      w: dimensions[widget].w,
      h: dimensions[widget].h,
      i: uuidv4(),
      static: true,
      widget: widget,
      settings: settings[widget],
    };

    getters.layout.push(item);
  },

  removeWidget({ commit, getters }, index) {
    if (getters.layout.length == 1) {
      commit('updateErrorMessage', 'Failed to Remove Widget');
      return;
    }

    const widgetIndex = getters.layout.findIndex(widget => widget.i === index);
    getters.layout.splice(widgetIndex, 1);

    commit('updateErrorMessage', '');
    return;
  },
};

export const mutations = {
  updateDashboards(state, data) {
    state.dashboards = data;
  },
  updateLayout(state, data) {
    state.layout = data;
  },
  updateCurrentDashboardId(state, data) {
    state.currentDashboardId = data;
  },
  updateErrorMessage(state, data) {
    state.errorMessage = data;
  },
};

export const getters = {
  colNum(state) {
    return state.colNum;
  },
  dashboards(state) {
    return state.dashboards;
  },
  dashboardOptions(state) {
    return state.dashboards
      ? state.dashboards.map(dashboard => {
          return {
            label: dashboard.name,
            value: dashboard.id,
            ...(dashboard.isPublic
              ? {
                  badgeText: 'Public',
                  badgeColor: 'warning',
                }
              : {
                  badgeText: 'Private',
                  badgeColor: 'primary',
                }),
          };
        })
      : [];
  },
  currentDashboardId(state) {
    return state.currentDashboardId;
  },

  currentDashboard(state) {
    return (
      state.dashboards &&
      state.dashboards.find(dashboard => dashboard.id === state.currentDashboardId)
    );
  },

  layout(state, getters) {
    return getters.currentDashboard && getters.currentDashboard.layout;
  },

  errorMessage(state) {
    return state.errorMessage;
  },
};
